// src/pages/clientEdit/ClientIntakeSummaryPage.js
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Card, Form, Spinner } from "react-bootstrap";
import api from "../api";
import { useNavGuard } from "../hooks/useNavGuard";
import NavGuardModal from "../components/NavGuardModal";
import AutoGrowTextarea from "../components/AutoGrowTextarea";

export default function ClientIntakeSummaryPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [dirty, setDirty] = useState(false);

  const [intakeSummary, setIntakeSummary] = useState("");
  const [navRecommended, setNavRecommended] = useState(null);
  const [navNotes, setNavNotes] = useState("");

  const { showModal, resolveGuard } = useNavGuard(dirty);

  function mark() { setDirty(true); setSuccess(""); }

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setError("");
      setSuccess("");
      try {
        const res = await api.get(`/clients/${id}`);
        if (!mounted) return;
        const c = res.data || {};
        setIntakeSummary(c.intake_summary ?? "");
        setNavRecommended(c.nav_program_recommended ?? null);
        setNavNotes(c.nav_program_notes ?? "");
        setDirty(false);
      } catch (e) {
        if (!mounted) return;
        setError(e?.response?.data?.message || e?.message || "Failed to load client.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => { mounted = false; };
  }, [id]);

  const doSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await api.put(`/clients/${id}`, {
        intake_summary: intakeSummary,
        nav_program_recommended: navRecommended,
        nav_program_notes: navNotes,
      });
      setDirty(false);
      setSuccess("Saved.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return true;
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to save.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    doSave();
  };

  if (loading) {
    return (
      <div className="p-2">
        <Spinner animation="border" size="sm" /> Loading...
      </div>
    );
  }

  return (
    <>
      <NavGuardModal
        show={showModal}
        saving={saving}
        error={error}
        onSave={async () => { const ok = await doSave(); if (ok) resolveGuard(true); }}
        onDiscard={() => { setDirty(false); resolveGuard(true); }}
        onCancel={() => resolveGuard(false)}
      />

      <Form onSubmit={handleSave}>
        {error && !showModal ? <Alert variant="danger">{error}</Alert> : null}
        {success ? <Alert variant="success">{success}</Alert> : null}

        <Card className="mb-3">
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label>Intake Summary</Form.Label>
              <AutoGrowTextarea
                rows={12}
                value={intakeSummary}
                onChange={(e) => { setIntakeSummary(e.target.value); mark(); }}
                placeholder="Enter intake summary..."
              />
            </Form.Group>

            <div className="d-flex gap-2">
              <Button type="submit" variant="primary" disabled={saving || !dirty}>
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" /> Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
              <Button
                type="button"
                variant="outline-secondary"
                onClick={() => navigate(-1)}
                disabled={saving}
              >
                Back
              </Button>
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>Navigation Program</Card.Header>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">
                Do you recommend this person for the Navigation program?
              </Form.Label>
              <div className="d-flex gap-4">
                {[
                  { label: "Yes", value: true },
                  { label: "No", value: false },
                ].map(({ label, value }) => (
                  <Form.Check
                    key={label}
                    type="radio"
                    id={`nav-recommended-${label}`}
                    name="nav-recommended"
                    label={label}
                    checked={navRecommended === value}
                    onChange={() => { setNavRecommended(value); mark(); }}
                  />
                ))}
              </div>
            </Form.Group>

            <Form.Group>
              <Form.Label>Notes</Form.Label>
              <AutoGrowTextarea
                rows={4}
                value={navNotes}
                onChange={(e) => { setNavNotes(e.target.value); mark(); }}
                placeholder="Navigation program notes..."
              />
            </Form.Group>
          </Card.Body>
        </Card>
      </Form>
    </>
  );
}
